import {defineField, defineType} from 'sanity'
import ScoringWeightsInput from '../../components/ScoringWeightsInput'
import ScoringInfoInput from '../../components/ScoringInfoInput'

export default defineType({
  name: 'buyerGuidesTemplate',
  title: 'Settings',
  type: 'document',
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    {
      name: 'scoring',
      title: 'Scoring',
    },
  ],
  fields: [
    defineField({
      title: 'Available designs',
      name: 'availableDesigns',
      type: 'object',
      group: 'content',
      description: 'Title and description above "Available designs" section on buyer guide',
      fields: [
        {
          title: 'Title',
          name: 'title',
          type: 'string',
        },
        {
          title: 'Description',
          name: 'description',
          type: 'text',
        },
      ],
    }),
    defineField({
      name: 'contact',
      title: 'Contact',
      description: 'Contact information on buyer guides page',
      type: 'contactBlock',
      group: 'content',
    }),
    defineField({
      name: 'scoringMaxResult',
      title: 'Max Score',
      description: 'Maximum score for scoring criteria',
      type: 'number',
      group: 'scoring',
      validation: (Rule) => Rule.required().positive(),
    }),
    defineField({
      name: 'scoringInfo',
      title: 'Scoring description',
      description:
        'Description shown in the info modal next to the "Scoring" heading on each fitment item. Provide a separate description per fitment category.',
      type: 'array',
      group: 'scoring',
      components: {
        input: ScoringInfoInput,
      },
      of: [
        {
          type: 'object',
          fields: [
            {
              name: 'fitment',
              type: 'reference',
              to: [{type: 'fitment'}],
            },
            {
              name: 'description',
              type: 'text',
            },
          ],
        },
      ],
    }),
    defineField({
      name: 'scoringWeights',
      title: 'Scoring Weights',
      type: 'array',
      group: 'scoring',
      components: {
        input: ScoringWeightsInput,
      },
      of: [
        {
          type: 'object',
          fields: [
            {
              name: 'fitment',
              type: 'reference',
              to: [{type: 'fitment'}],
            },
            {
              name: 'scoreCriteria',
              type: 'reference',
              to: [{type: 'scoringCriteria'}],
            },
            {
              name: 'weight',
              type: 'number',
            },
          ],
        },
      ],
    }),
    defineField({
      name: 'performanceScoreWeights',
      title: 'Performance Score Weights',
      type: 'object',
      group: 'scoring',
      description:
        'Weights for each performance sub-score. Must total 100%. Default: equal weights (25% each).',
      fields: [
        {
          name: 'wheelWidth',
          title: 'Wheel Width Weight (%)',
          type: 'number',
          initialValue: 25,
          validation: (Rule) => Rule.required().min(0).max(100),
        },
        {
          name: 'tireWidth',
          title: 'Tire Width Weight (%)',
          type: 'number',
          initialValue: 25,
          validation: (Rule) => Rule.required().min(0).max(100),
        },
        {
          name: 'tireCategory',
          title: 'Tire Category Weight (%)',
          type: 'number',
          initialValue: 25,
          validation: (Rule) => Rule.required().min(0).max(100),
        },
        {
          name: 'diameter',
          title: 'Wheel Diameter Weight (%)',
          type: 'number',
          initialValue: 25,
          validation: (Rule) => Rule.required().min(0).max(100),
        },
      ],
    }),
    defineField({
      title: 'Filter groups description',
      name: 'filterInfo',
      type: 'object',
      group: 'content',
      description: 'Description used to explain a given filter group. Keep brief and explanatory.',
      fields: [
        {
          title: 'Diameter',
          name: 'diameter',
          type: 'text',
        },
        {
          title: 'Front Width',
          name: 'frontWidth',
          type: 'text',
        },
        {
          title: 'Rear Width',
          name: 'rearWidth',
          type: 'text',
        },
        {
          title: 'Front Tire Width',
          name: 'frontTireWidth',
          type: 'text',
        },
        {
          title: 'Rear Tire Width',
          name: 'rearTireWidth',
          type: 'text',
        },
        {
          title: 'Construction',
          name: 'construction',
          type: 'text',
        },
        {
          title: 'Fitment Types',
          name: 'fitmentTypes',
          type: 'text',
        },
        {
          title: 'Tire Types',
          name: 'tireTypes',
          type: 'text',
        },
        {
          title: 'Other Filters',
          name: 'otherFilters',
          type: 'text',
        },
        {
          title: 'Acceptable Modifications',
          name: 'modifications',
          type: 'text',
        },
      ],
    }),
  ],
  preview: {
    prepare() {
      return {
        title: 'Buyer Guides Template',
      }
    },
  },
})
